import { FormikErrors, useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import { get } from 'lodash';

import { Badge } from '@@/Badge';

import { artifactConfigStepValidationSchema } from '../../validation';
import { WizardMode, WizardStep, useWizardContext } from '../../WizardContext';
import { StepTitle } from '../StepTitle';
import { ArtifactConfig, FormValues } from '../../types';
import {
  DeploymentType,
  DEPLOYMENT_TYPE_LABELS,
  UNKNOWN_DEPLOYMENT_TYPE,
} from '../../detectDeploymentType';
import { useDetectDeploymentType } from '../../useDetectDeploymentType';

import { AdvancedConfiguration } from './AdvancedConfiguration';
import { TargetsSelector } from './TargetsSelector';
import { TargetsTree } from './TargetsTree';
import { UpdateConfiguration } from './UpdateConfiguration';
import { EnvironmentVariables } from './EnvironmentVariables';
import { RegistryConfiguration } from './RegistryConfiguration';
import { ComposeDeployOptions } from './ComposeDeployOptions';

/** One config step per artifact, so each is configured on its own screen. */
export function createArtifactConfigStep(index: number): WizardStep {
  return {
    id: `config-${index}`,
    label: `Config ${index + 1}`,
    Component: () => <ArtifactConfigSection index={index} />,
    validateStep: () => artifactConfigStepValidationSchema(index),
  };
}

function ArtifactConfigSection({ index }: { index: number }) {
  const isEditing = useWizardContext().mode === WizardMode.Edit;

  // The engine is a workflow-level choice, so it is read from the root form
  // rather than from the artifact this section is scoped to.
  const { engine } = useFormikContext<FormValues>().values;

  const {
    values,
    errors: artifactErrors,
    setFieldValue,
    setFieldError,
    root,
  } = useNamespacedFormikContext<
    FormValues,
    FormValues['artifacts'][number],
    Exclude<FormikErrors<FormValues>['artifacts'], string | undefined>[number]
  >(`artifacts[${index}]`);

  const errors =
    typeof artifactErrors === 'string' ? undefined : artifactErrors;

  // Detection reads the first file to tell Compose from Kubernetes. It runs for
  // both engines, but answers a different question for each: which type an Edge
  // stack is, and whether a reconciled artifact is one its engine cannot take.
  const isReconciled = engine === 'reconcile';

  const { isLoading, data } = useDetectDeploymentType(values.files[0]);
  useEffect(() => {
    if (isLoading || !data) {
      return;
    }

    // A reconciled artifact is Kubernetes by definition, and its paths may be a
    // directory or a chart that detection cannot read - so anything short of a
    // definite Compose answer is recorded as Kubernetes rather than left
    // unknown. Compose is what the schema refuses on, and recording the current
    // file's answer every time is what lets a corrected selection clear it.
    if (isReconciled) {
      setFieldValue(
        'deploymentType',
        data === DeploymentType.Compose
          ? DeploymentType.Compose
          : DeploymentType.Kubernetes
      );

      return;
    }

    setFieldValue('deploymentType', data);
  }, [isReconciled, data, isLoading, setFieldValue]);

  const setConfigValue = useCallback(
    <K extends keyof ArtifactConfig>(f: K, v: ArtifactConfig[K]) =>
      setFieldValue('config', { ...values.config, [f]: v }),
    [values.config, setFieldValue]
  );

  // A reconciled artifact is only stopped by a definite Compose answer, and is
  // not made to wait for one: it already knows what it is, and its paths may
  // well be ones detection answers nothing for.
  if (isReconciled && data === DeploymentType.Compose) {
    return (
      <div className="flex flex-col">
        The reconciliation engine deploys Kubernetes resources only, and{' '}
        {values.files[0]?.path} is a Docker Compose file. Go back and select
        Kubernetes manifests, a directory of them, or a Helm chart.
      </div>
    );
  }

  if (!isReconciled) {
    if (isLoading || !data) {
      return null;
    }

    if (data === UNKNOWN_DEPLOYMENT_TYPE) {
      return (
        <div className="flex flex-col">
          Unable to determine deployment type. The selected file does not look
          like a valid Docker Compose or Kubernetes manifest.
        </div>
      );
    }
  }

  return (
    <section aria-label={`Configure ${values.name}`}>
      <StepTitle
        title={
          <>
            Configure {values.name}{' '}
            <Badge>{DEPLOYMENT_TYPE_LABELS[values.deploymentType]}</Badge>
          </>
        }
        subtitle="Choose the deployment targets and settings for this stack."
      />

      <div className="flex flex-col gap-4">
        {engine === 'reconcile' ? (
          <TargetsTree
            value={{
              edgeGroups: values.targets,
              environmentGroups: values.environmentGroups,
              environments: values.environments,
            }}
            // Written as one update rather than three. Formik validates each
            // setFieldValue against the values the component was rendered
            // with, so three in a row would validate the last field against a
            // form still missing the other two, and picking a single target
            // would read as having picked none.
            onChange={(next) =>
              root.setFieldValue(`artifacts[${index}]`, {
                ...values,
                targets: next.edgeGroups,
                environmentGroups: next.environmentGroups,
                environments: next.environments,
              })
            }
          />
        ) : (
          <TargetsSelector
            values={values.targets}
            onChange={(targets) => setFieldValue('targets', targets)}
            deploymentType={values.deploymentType}
          />
        )}

        <AdvancedConfiguration
          values={values.config}
          setFieldValue={setConfigValue}
          refName={values.files[0]?.ref}
          sourceId={values.files[0]?.sourceId}
          errors={errors?.config}
          deploymentType={values.deploymentType}
          isEditing={isEditing}
          engine={engine}
        />

        {values.deploymentType === DeploymentType.Compose && (
          <EnvironmentVariables
            values={values.config.envVars}
            onChange={(value) => setConfigValue('envVars', value)}
            errors={errors?.config?.envVars}
          />
        )}

        {/* Registry credentials and the staggered rollout are how an Edge
            stack is deployed. A reconciled deployment is published as desired
            state and applied by the environment on its own schedule, so
            neither has anything to act on. */}
        {!isReconciled && (
          <RegistryConfiguration
            values={values.config}
            setFieldValue={(f, v) => setConfigValue(f, v)}
            onRegistryError={(message) =>
              setFieldError(`config.registryId`, message)
            }
            errors={errors?.config}
          />
        )}

        {values.deploymentType === DeploymentType.Compose && (
          <ComposeDeployOptions
            values={values.config}
            setFieldValue={setConfigValue}
          />
        )}

        {!isReconciled && (
          <UpdateConfiguration
            values={values.config.parallelConfig}
            onChange={(config) =>
              setConfigValue('parallelConfig', {
                ...values.config.parallelConfig,
                ...config,
              })
            }
            errors={errors?.config?.parallelConfig}
          />
        )}
      </div>
    </section>
  );
}

function useNamespacedFormikContext<T, K, E>(namespace: string) {
  const {
    values: rootValues,
    errors: rootErrors,
    setFieldValue: rootSetFieldValue,
    setFieldError: rootSetFieldError,
    ...context
  } = useFormikContext<T>();

  const setFieldValue: typeof rootSetFieldValue = useCallback(
    (field, ...params) => rootSetFieldValue(`${namespace}.${field}`, ...params),
    [namespace, rootSetFieldValue]
  );

  const setFieldError: typeof rootSetFieldError = useCallback(
    (field, ...params) => rootSetFieldError(`${namespace}.${field}`, ...params),
    [namespace, rootSetFieldError]
  );

  const values: K = useMemo(
    () => get(rootValues, namespace),
    [namespace, rootValues]
  );
  const errors: E = useMemo(
    () => get(rootErrors, namespace),
    [namespace, rootErrors]
  );

  return useMemo(
    () => ({
      ...context,
      values,
      errors,
      setFieldValue,
      setFieldError,
      root: {
        values: rootValues,
        errors: rootErrors,
        setFieldValue: rootSetFieldValue,
        setFieldError: rootSetFieldError,
      },
    }),
    [
      context,
      errors,
      rootErrors,
      rootSetFieldError,
      rootSetFieldValue,
      rootValues,
      setFieldError,
      setFieldValue,
      values,
    ]
  );
}
